import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import * as XLSX from "xlsx";
import db from "./Firebase";

const parseDate = (val) => {
    if (!val) return null;
    if (typeof val?.toDate === "function") return val.toDate();
    if (val instanceof Date) return val;
    if (typeof val?.seconds === "number") return new Date(val.seconds * 1000);
    const d = new Date(val);
    return isNaN(d) ? null : d;
};

const fmtDate = (val) => {
    const d = parseDate(val);
    if (!d) return "-";
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const fmtDateTime = (val) => {
    const d = parseDate(val);
    if (!d) return "-";
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const compactPhone = (val) => String(val || "").replace(/\D/g, "").slice(-10);
const cleanText = (val, fb = "-") => (val === null || val === undefined || val === "" ? fb : String(val));

const JAN_2025 = new Date("2025-01-01T00:00:00");

// Add phone numbers here to show "Test User" in Earlier Plan column
const TEST_PHONES = new Set([
    "7856946583",
    "9315301306",
    "9810255895",
    "9717815164",
    "8171461502",
    "9354405527",
    "7508294015",
]);

export default function ShikshaMembershipUsers() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [logModal, setLogModal] = useState(null);
    const [workshopModal, setWorkshopModal] = useState(null);
    const [filterStatus, setFilterStatus] = useState("all");
    const [activeTab, setActiveTab] = useState("all");

    useEffect(() => {
        const fetchAll = async () => {
            setLoading(true);
            try {
                const [snap, glowSnap, wsSnap] = await Promise.all([
                    getDocs(collection(db, "nv_shiksha_subscriptions")),
                    getDocs(collection(db, "nv_glow_membership")),
                    getDocs(collection(db, "workshop_registration")),
                ]);

                const SEP_11_2026 = new Date("2026-09-11T23:59:59");

                // phone → price + all membership periods from nv_glow_membership
                const glowPriceMap = {};
                const glowRecords = {}; // phone → [{start, end}]
                glowSnap.forEach(docSnap => {
                    const d = docSnap.data();
                    const phone = compactPhone(d.phone_no || docSnap.id);
                    if (!phone) return;
                    glowPriceMap[phone] = cleanText(d.price);
                    if (!glowRecords[phone]) glowRecords[phone] = [];
                    glowRecords[phone].push({ start: parseDate(d.start_date), end: parseDate(d.end_date) });
                });

                // phone → all shiksha membership periods (first pass over snap)
                const shikshaRecords = {};
                snap.forEach(docSnap => {
                    const d = docSnap.data();
                    const phone = compactPhone(d.phone_no);
                    if (!phone) return;
                    if (!shikshaRecords[phone]) shikshaRecords[phone] = [];
                    shikshaRecords[phone].push({ start: parseDate(d.start_date), end: parseDate(d.end_date) });
                });

                // Build workshop counts + detail per phone (Jan 2025+)
                const phoneCounts = {};
                const phoneWorkshops = {};
                wsSnap.forEach(docSnap => {
                    const d = docSnap.data();
                    const dt = parseDate(d.created_date);
                    if (!dt || dt < JAN_2025) return;
                    const phone = compactPhone(d.phone);
                    if (!phone || phone.length < 10) return;
                    const wsDate = parseDate(d.workshop_Date || d.created_date);
                    let memberAtTime = null;
                    if (wsDate) {
                        if (wsDate <= SEP_11_2026) {
                            memberAtTime = (glowRecords[phone] || []).some(
                                r => r.start && r.end && wsDate >= r.start && wsDate <= r.end
                            );
                        } else {
                            memberAtTime = (shikshaRecords[phone] || []).some(
                                r => r.start && r.end && wsDate >= r.start && wsDate <= r.end
                            );
                        }
                    }
                    phoneCounts[phone] = (phoneCounts[phone] || 0) + 1;
                    if (!phoneWorkshops[phone]) phoneWorkshops[phone] = [];
                    phoneWorkshops[phone].push({
                        name: d.workshop_event_name || "-",
                        desc: d.description || "-",
                        date: d.workshop_Date || d.created_date,
                        location: d.workshop_location || "-",
                        payment: d.payment || "-",
                        memberAtTime,
                    });
                });

                const data = [];
                snap.forEach(docSnap => {
                    const d = docSnap.data();
                    const phone = compactPhone(d.phone_no || docSnap.id);
                    data.push({
                        id: docSnap.id,
                        phone,
                        name: cleanText(d.name),
                        membershipType: cleanText(d.membership_type),
                        price: d.price ?? "-",
                        startDate: d.start_date || null,
                        endDate: d.end_date || null,
                        createdDate: d.created_date || null,
                        status: cleanText(d.status),
                        tag: cleanText(d.tag),
                        subscriptionLog: Array.isArray(d.subscription_log) ? d.subscription_log : [],
                        earlierPlan: (() => {
                            if (TEST_PHONES.has(phone)) return "Test User";
                            const cd = parseDate(d.created_date);
                            if (cd && cd > new Date("2026-09-11T23:59:59")) return "New Enrollment";
                            return glowPriceMap[phone] || "-";
                        })(),
                        workshopCount: phoneCounts[phone] || 0,
                        workshops: (phoneWorkshops[phone] || []).sort((a, b) =>
                            (parseDate(b.date)?.getTime() || 0) - (parseDate(a.date)?.getTime() || 0)
                        ),
                    });
                });
                // Fetch onboarded date from personal_details — only for phones in this list
                const { query: fsQuery, where } = await import("firebase/firestore");
                const uniquePhones = [...new Set(data.map(r => r.phone).filter(p => p && p.length === 10))];
                const onboardedMap = {};
                for (let i = 0; i < uniquePhones.length; i += 10) {
                    const chunk = uniquePhones.slice(i, i + 10);
                    try {
                        const pdSnap = await getDocs(fsQuery(collection(db, "personal_details"), where("phone_no", "in", chunk)));
                        pdSnap.forEach(docSnap => {
                            const d = docSnap.data();
                            const ph = compactPhone(d.phone_no);
                            if (ph) onboardedMap[ph] = d.created_date || null;
                        });
                    } catch (_) { }
                }
                data.forEach(r => { r.onboardedDate = onboardedMap[r.phone] || null; });

                data.sort((a, b) => (parseDate(b.createdDate)?.getTime() || 0) - (parseDate(a.createdDate)?.getTime() || 0));
                setRows(data);
            } catch (err) {
                console.error("ShikshaMembershipUsers fetch error:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, []);

    const today = new Date();

    const baseFiltered = rows.filter(r => {
        const q = search.trim().toLowerCase();
        const matchSearch = !q ||
            r.phone.includes(q) ||
            r.name.toLowerCase().includes(q) ||
            r.membershipType.toLowerCase().includes(q) ||
            r.status.toLowerCase().includes(q);

        const endDate = parseDate(r.endDate);
        const isActive = endDate && endDate > today;
        const matchStatus =
            filterStatus === "all" ||
            (filterStatus === "active" && isActive) ||
            (filterStatus === "expired" && !isActive);

        const validTypes = ["shiksha", "shiksha_plus_monthly", "shiksha_plus_yearly"];
        const matchType = validTypes.includes(r.membershipType);
        const matchEarlierPlan = !r.earlierPlan.startsWith("₹0");

        return matchSearch && matchStatus && matchType && matchEarlierPlan;
    });

    const tabCounts = {
        all: baseFiltered.length,
        monthly: baseFiltered.filter(r => r.membershipType === "shiksha_plus_monthly").length,
        yearly: baseFiltered.filter(r => r.membershipType === "shiksha_plus_yearly").length,
        shiksha: baseFiltered.filter(r => r.membershipType === "shiksha").length,
    };
    const filtered = activeTab === "all" ? baseFiltered
        : activeTab === "monthly" ? baseFiltered.filter(r => r.membershipType === "shiksha_plus_monthly")
            : activeTab === "yearly" ? baseFiltered.filter(r => r.membershipType === "shiksha_plus_yearly")
                : baseFiltered.filter(r => r.membershipType === "shiksha");

    const handleExport = () => {
        const sheetData = filtered.map((r, i) => ({
            "#": i + 1,
            "Name": r.name,
            "Phone No": r.phone,
            "Membership Type": r.membershipType,
            "Price": r.price,
            "Start Date": fmtDate(r.startDate),
            "End Date": fmtDate(r.endDate),
            "Status": r.status,
            "Tag": r.tag,
            "Earlier Plan": r.earlierPlan,
            "Onboarded Date": fmtDate(r.onboardedDate),
        }));

        // Tab 2 — subscription log details
        const logData = [];
        filtered.forEach(r => {
            r.subscriptionLog.forEach((entry, i) => {
                logData.push({
                    "Phone No": r.phone,
                    "Name": r.name,
                    "Log #": i + 1,
                    "Membership Type": cleanText(entry.membership_type),
                    "Price": entry.price ?? "-",
                    "Tag": cleanText(entry.tag),
                });
            });
        });

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheetData), "Shiksha Members");
        if (logData.length > 0) {
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(logData), "Subscription Log");
        }
        XLSX.writeFile(wb, "shiksha_membership_users.xlsx");
    };

    const activeCount = rows.filter(r => { const d = parseDate(r.endDate); return d && d > today; }).length;
    const expiredCount = rows.length - activeCount;

    return (
        <>
            <div className="container-fluid py-4">

                {/* HEADER */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                        <h3 className="mb-1">Shiksha Membership Users</h3>
                        {/* <div className="text-muted" style={{ fontSize: "13px" }}>
                            nv_shiksha_subscriptions · {rows.length} total records
                        </div> */}
                    </div>
                    <Link to="/" className="btn btn-outline-secondary btn-sm">Back</Link>
                </div>


                {/* SEARCH + FILTER + EXPORT */}
                <div className="card mb-3">
                    <div className="card-body py-3 px-4">
                        <div className="d-flex gap-3 align-items-center flex-wrap">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search by name, phone, type or status…"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                style={{ maxWidth: "300px" }}
                            />
                            <select
                                className="form-select"
                                value={filterStatus}
                                onChange={e => setFilterStatus(e.target.value)}
                                style={{ maxWidth: "160px" }}
                            >
                                <option value="all">All</option>
                                <option value="active">Active Only</option>
                                <option value="expired">Expired Only</option>
                            </select>
                            {(search || filterStatus !== "all") && (
                                <span className="text-muted" style={{ fontSize: "13px" }}>
                                    {filtered.length} result{filtered.length !== 1 ? "s" : ""}
                                </span>
                            )}
                            <button
                                className="btn btn-success ms-auto"
                                onClick={handleExport}
                                disabled={loading || filtered.length === 0}
                                style={{ minWidth: "140px", fontWeight: 700 }}
                            >
                                Export Excel
                            </button>
                        </div>
                    </div>
                </div>

                {/* TABS */}
                {!loading && (
                    <ul className="nav nav-tabs mb-3">
                        {[
                            { key: "all", label: "All Plans", badgeClass: "bg-secondary" },
                            { key: "monthly", label: "Shiksha Plus Monthly", badgeClass: "bg-info text-dark" },
                            { key: "yearly", label: "Shiksha Plus Yearly", badgeClass: "bg-primary" },
                            { key: "shiksha", label: "Shiksha", badgeClass: "bg-success" },
                        ].map(tab => (
                            <li className="nav-item" key={tab.key}>
                                <button
                                    className={`nav-link ${activeTab === tab.key ? "active fw-bold" : ""}`}
                                    onClick={() => setActiveTab(tab.key)}
                                    style={activeTab !== tab.key ? { color: "#fff" } : {}}
                                >
                                    {tab.label}
                                    <span className={`badge ${tab.badgeClass} ms-2`} style={{ fontSize: "11px" }}>
                                        {tabCounts[tab.key]}
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}

                {/* TABLE */}
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status" />
                        <div className="mt-2 text-muted" style={{ fontSize: "14px" }}>Loading…</div>
                    </div>
                ) : (
                    <div className="card">
                        <div className="table-responsive">
                            <table className="table table-bordered table-hover align-middle mb-0" style={{ fontSize: "13px" }}>
                                <thead className="table-dark">
                                    <tr>
                                        <th>#</th>
                                        <th>Name</th>
                                        <th>Phone No</th>
                                        <th>Membership Type</th>
                                        <th className="text-end">Price</th>
                                        <th>Start Date</th>
                                        <th>End Date</th>
                                        <th className="text-center">Status</th>
                                        <th>Earlier Plan</th>
                                        <th>Onboarded Date</th>
                                        <th className="text-center">History</th>
                                        <th className="text-center">Workshops (Jan 2025+)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.length === 0 ? (
                                        <tr>
                                            <td colSpan="12" className="text-center py-4 text-muted">
                                                {search || filterStatus !== "all" ? "No results found" : "No data found"}
                                            </td>
                                        </tr>
                                    ) : filtered.map((row, i) => {
                                        const endDate = parseDate(row.endDate);
                                        const isActive = endDate && endDate > today;
                                        return (
                                            <tr key={row.id}>
                                                <td>{i + 1}</td>
                                                <td style={{ fontWeight: 600 }}>{row.name}</td>
                                                <td>{row.phone}</td>
                                                <td>
                                                    <span className="badge bg-info text-dark" style={{ fontSize: "10px", padding: "3px 6px", fontWeight: 600 }}>
                                                        {row.membershipType}
                                                    </span>
                                                </td>
                                                <td className="text-end fw-bold" style={{ color: "#16a34a" }}>
                                                    {row.price !== "-" ? `₹${row.price}` : "-"}
                                                </td>
                                                <td style={{ whiteSpace: "nowrap" }}>{fmtDate(row.startDate)}</td>
                                                <td style={{ whiteSpace: "nowrap" }}>
                                                    <span className={`badge ${isActive ? "bg-success" : "bg-danger"}`}
                                                        style={{ fontSize: "12px", padding: "5px 10px" }}>
                                                        {fmtDate(row.endDate)}
                                                    </span>
                                                </td>
                                                <td className="text-center">
                                                    <span className={`badge ${isActive ? "bg-success" : "bg-secondary"}`}
                                                        style={{ fontSize: "12px", padding: "5px 10px" }}>
                                                        {isActive ? "Active" : "Expired"}
                                                    </span>
                                                </td>
                                                <td style={{
                                                    whiteSpace: "nowrap", fontWeight: 600,
                                                    color: row.earlierPlan === "Test User" ? "#dc2626" : "#7c3aed"
                                                }}>
                                                    {row.earlierPlan !== "-" ? row.earlierPlan : <span className="text-muted fw-normal">-</span>}
                                                </td>

                                                <td style={{ whiteSpace: "nowrap" }}>{fmtDate(row.onboardedDate)}</td>
                                                <td className="text-center">
                                                    {row.subscriptionLog.length > 0 ? (
                                                        <span
                                                            className="badge bg-warning text-dark"
                                                            style={{ cursor: "pointer", fontSize: "13px", padding: "5px 10px", fontWeight: 700 }}
                                                            onClick={() => setLogModal(row)}
                                                            title="Click to view history"
                                                        >
                                                            {row.subscriptionLog.length}x
                                                        </span>
                                                    ) : <span className="text-muted">-</span>}
                                                </td>
                                                <td className="text-center">
                                                    {row.workshopCount > 0 ? (
                                                        <span
                                                            className="badge bg-primary"
                                                            style={{ cursor: "pointer", fontSize: "13px", padding: "5px 10px" }}
                                                            onClick={() => setWorkshopModal(row)}
                                                            title="Click to see workshops"
                                                        >
                                                            {row.workshopCount}
                                                        </span>
                                                    ) : (
                                                        <span className="text-muted">0</span>
                                                    )}
                                                </td>

                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {/* WORKSHOP DETAIL MODAL */}
            {workshopModal && (
                <div
                    onClick={() => setWorkshopModal(null)}
                    style={{
                        position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
                        zIndex: 9999, display: "flex", alignItems: "center",
                        justifyContent: "center", padding: "20px",
                    }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            background: "#fff", borderRadius: "8px", width: "100%",
                            maxWidth: "800px", maxHeight: "80vh",
                            display: "flex", flexDirection: "column",
                            boxShadow: "0 8px 40px rgba(0,0,0,0.25)",
                        }}
                    >
                        <div className="d-flex justify-content-between align-items-center px-4 py-3 border-bottom">
                            <div>
                                <div className="fw-bold" style={{ fontSize: "15px" }}>
                                    Workshops Attended ({workshopModal.workshopCount})
                                </div>
                                <div className="text-muted" style={{ fontSize: "12px" }}>
                                    {workshopModal.name} · {workshopModal.phone}
                                </div>
                            </div>
                            <button className="btn-close" onClick={() => setWorkshopModal(null)} />
                        </div>
                        <div style={{ overflowY: "auto", padding: "16px" }}>
                            <table className="table table-sm table-bordered align-middle mb-0" style={{ fontSize: "13px" }}>
                                <thead className="table-dark">
                                    <tr>
                                        <th>#</th>
                                        <th>Workshop Date</th>
                                        <th>Workshop</th>
                                        {/* <th>Location</th> */}
                                        {/* <th>Payment</th> */}
                                        <th className="text-center">Member at Time?</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {workshopModal.workshops.map((w, i) => (
                                        <tr key={i}>
                                            <td>{i + 1}</td>
                                            <td style={{ whiteSpace: "nowrap" }}>{fmtDate(w.date)}</td>
                                            <td>{w.name}</td>
                                            {/* <td>{w.location}</td> */}
                                            {/* <td>{w.payment}</td> */}
                                            <td className="text-center">
                                                {w.memberAtTime === null ? (
                                                    <span className="text-muted">-</span>
                                                ) : (
                                                    <span className={`badge ${w.memberAtTime ? "bg-success" : "bg-danger"}`}
                                                        style={{ fontSize: "11px", padding: "4px 8px" }}>
                                                        {w.memberAtTime ? "✓ Yes" : "✗ No"}
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* SUBSCRIPTION LOG MODAL */}
            {logModal && (
                <div
                    onClick={() => setLogModal(null)}
                    style={{
                        position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
                        zIndex: 9999, display: "flex", alignItems: "center",
                        justifyContent: "center", padding: "20px",
                    }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            background: "#fff", borderRadius: "8px", width: "100%",
                            maxWidth: "600px", maxHeight: "80vh",
                            display: "flex", flexDirection: "column",
                            boxShadow: "0 8px 40px rgba(0,0,0,0.25)",
                        }}
                    >
                        <div className="d-flex justify-content-between align-items-center px-4 py-3 border-bottom">
                            <div>
                                <div className="fw-bold" style={{ fontSize: "15px" }}>
                                    History ({logModal.subscriptionLog.length}x)
                                </div>
                                <div className="text-muted" style={{ fontSize: "12px" }}>
                                    {logModal.name} · {logModal.phone}
                                </div>
                            </div>
                            <button className="btn-close" onClick={() => setLogModal(null)} />
                        </div>
                        <div style={{ overflowY: "auto", padding: "16px" }}>
                            <table className="table table-sm table-bordered align-middle mb-0" style={{ fontSize: "13px" }}>
                                <thead className="table-dark">
                                    <tr>
                                        <th>#</th>
                                        <th>Created Date</th>
                                        <th>Membership Type</th>
                                        <th className="text-end">Price</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[...logModal.subscriptionLog].sort((a, b) =>
                                        (parseDate(b.created_date)?.getTime() || 0) - (parseDate(a.created_date)?.getTime() || 0)
                                    ).map((entry, i) => (
                                        <tr key={i}>
                                            <td>{i + 1}</td>
                                            <td style={{ whiteSpace: "nowrap" }}>{fmtDateTime(entry.created_date)}</td>
                                            <td>
                                                <span className="badge bg-info text-dark" style={{ fontSize: "12px" }}>
                                                    {cleanText(entry.membership_type)}
                                                </span>
                                            </td>
                                            <td className="text-end fw-bold">
                                                {entry.price !== undefined ? `₹${entry.price}` : "-"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
